import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useEffect, useId, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import MarketingImage from '#app/components/marketing/image.tsx'
import { LandingSection } from '#app/components/marketing/landing-section.tsx'
import MarketingSpacer from '#app/components/marketing/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Text } from '#app/components/ui/typography.tsx'
import { useChangeBackgroundColor } from '#app/hooks/use-change-background-color.ts'
import { usePublicContext } from '#app/routes/($lang)+/__public-context.tsx'
import { BASE_URL } from '#app/utils/config.ts'
import { extendedTheme } from '#app/utils/extended-theme.ts'
import { generateMetaTranslationLoader } from '#app/utils/i18next.server.ts'
import { useDesktop } from '#app/utils/media-query.ts'
import { generateTranslatedMeta } from '#app/utils/meta.ts'
import { cn } from '#app/utils/misc.tsx'
import { useIsMounted } from '#app/hooks/use-is-mounted.ts'
import routes from '#routes/index.tsx'
import { type Route } from '../../../../.react-router/types/app/routes/($lang)+/_marketing+/+types/dress-code.ts'
import ICGGalleryMilano from '/img/dress-your-look/ICG GALLERY MILANO.svg'
import CasaDecor2025 from '/img/dress-your-look/CASADECOR-2025.svg'

export async function loader({ request }: Route.LoaderArgs) {
	const catalog = {
		path: 'cataloghi_catalogues/sapienstone_dress_code1-SSDC.pdf',
	}

	return {
		catalog,
		meta: await generateMetaTranslationLoader({
			request,
			namespace: 'dressCode',
			keys: {
				title: 'title',
				description: 'about.description',
				canonical: `${BASE_URL}${routes.public.landings.dressYourLook.route({})}`,
			},
		}),
	}
}

export const meta = generateTranslatedMeta

type SectionsType = 'size' | 'thickness' | 'finish' | 'mosaic' | 'relief'

function CollectionTable<T extends readonly SectionsType[]>({
	className,
	product,
	sections,
	items,
}: {
	className?: string
	product?: ReactNode
	sections: T
	items: Record<T[number], string>[]
}) {
	const id = useId()
	const { t } = useTranslation('common')

	return (
		<motion.div
			key={id}
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -20 }}
			transition={{ duration: 0.3 }}
			className={cn('flex flex-col gap-md text-xs text-brand-dusk', className)}
		>
			{product}
			<div
				className={`grid gap-sm`}
				style={{
					gridTemplateColumns: `repeat(${sections.length}, 1fr)`,
				}}
			>
				{sections.map((section) => (
					<div key={section} className="flex flex-col gap-sm md:gap-md">
						<p className="border-b border-current font-semibold uppercase tracking-[0.2em]">
							{t(`product.${section}`)}
						</p>

						<div className={cn('flex flex-col gap-xxs font-light')}>
							{items.map((item, index) => (
								// @ts-ignore
								<p key={index}>{item[section] ?? ''}</p>
							))}
						</div>
					</div>
				))}
			</div>
		</motion.div>
	)
}

interface Collection<T extends readonly SectionsType[]> {
	id: string
	name: string
	images: {
		src: string
		className?: string
		area: {
			row: string
			column: string
		}
	}[]
	sections: T
	items: Record<T[number], string>[]
}

const fioriDiViolaCollection: Collection<
	['size', 'thickness', 'finish', 'relief']
>['items'] = [
	{
		size: '320x160cm | 126"x63"',
		thickness: '12mm | 20mm',
		finish: 'Cashmere',
		relief: '-',
	},
	{
		size: '120x120cm | 48"x48"',
		thickness: '8mm',
		finish: 'Cashmere',
		relief: '-',
	},
	{
		size: '120x60cm | 48"x24"',
		thickness: '8mm',
		finish: 'Structured',
		relief: 'Cresta*',
	},
]

const grigioSavelliCollection: Collection<
	['size', 'thickness', 'finish', 'relief']
>['items'] = [
	{
		size: '270x120cm | 106"x48"',
		thickness: '6mm',
		finish: 'Cashmere',
		relief: '-',
	},
	{
		size: '120x120cm | 48"x48"',
		thickness: '8mm',
		finish: 'Natural',
		relief: '-',
	},
	{
		size: '120x60cm | 48"x24"',
		thickness: '8mm',
		finish: 'Structured',
		relief: 'Velo*',
	},
]

const cremaNeveCollection: Collection<
	['size', 'thickness', 'finish', 'relief']
>['items'] = [
	{
		size: '270x120cm | 106"x48"',
		thickness: '6mm',
		finish: 'Structured',
		relief: '-',
	},
	{
		size: '120x120cm | 48"x48"',
		thickness: '8mm',
		finish: 'Natural',
		relief: '-',
	},
	{
		size: '120x60cm | 48"x24"',
		thickness: '8mm',
		finish: 'Structured',
		relief: 'Velo*',
	},
]

const bluBelgeCollection: Collection<
	['size', 'thickness', 'finish', 'relief']
>['items'] = [
	{
		size: '120x120cm | 48"x48"',
		thickness: '8mm',
		finish: 'Natural',
		relief: '-',
	},
	{
		size: '120x60cm | 48"x24"',
		thickness: '8mm',
		finish: 'Structured',
		relief: 'Filo*',
	},
]

const m1m2Collection: Collection<
	['size', 'thickness', 'finish', 'mosaic']
>['items'] = [
	{
		size: '26,2x26,2cm | 10 5/16"x 10 5/16"',
		thickness: '8mm',
		finish: 'Natural',
		mosaic: 'M1 (left image)',
	},
	{
		size: '26,2x26,2cm | 10 5/16"x 10 5/16"',
		thickness: '8mm',
		finish: 'Natural',
		mosaic: 'M2 (right image)',
	},
]

const vulcaCollection: Collection<['size', 'thickness', 'finish']>['items'] = [
	{
		size: '10x10cm | 4"x4"',
		thickness: '10mm',
		finish: 'Glossy',
	},
	{
		size: '120x15cm | 48"x6"',
		thickness: '10mm',
		finish: 'Glossy',
	},
	{
		size: '120x60cm | 48"x24"',
		thickness: '10mm',
		finish: 'Glossy',
	},
]

function ProductHoverSection<T extends readonly SectionsType[]>({
	collections,
}: {
	collections: Collection<T>[]
}) {
	const { t } = useTranslation('dressCode')
	const [selected, setSelected] = useState<Collection<T> | null>(null)
	const mounted = useIsMounted()
	const isDesktop = useDesktop()

	const handleMouseEnter = (c: string) => {
		const collection = collections.find((collection) => collection.id === c)

		if (!collection) return
		setSelected(collection)
	}

	if (!mounted || !isDesktop) {
		return null
	}

	const GRID_MEASURES = {
		GAP: 8,
		ROWS: 14,
		COLUMNS: 20,
		ROW_HEIGHT: 3,
		COLUMN_WIDTH: 1, // in fractional units
	}

	return (
		<LandingSection backgroundColor={'#000'} className={'text-white'}>
			<div className={'mx-auto flex max-w-[1400px] flex-col gap-md'}>
				<AnimatePresence mode="wait">
					{/* Collage Grid */}
					<div
						className="relative mb-xxl 2xl:mb-xl"
						style={{
							display: 'grid',
							gridTemplateColumns: `repeat(${GRID_MEASURES.COLUMNS}, ${GRID_MEASURES.COLUMN_WIDTH}fr)`,
							gridTemplateRows: `repeat(${GRID_MEASURES.ROWS}, ${GRID_MEASURES.ROW_HEIGHT}vh)`,
							gap: `${GRID_MEASURES.GAP}px`,
							minHeight: `calc(${GRID_MEASURES.ROWS} * ${GRID_MEASURES.ROW_HEIGHT}vh + ${GRID_MEASURES.ROWS} * ${GRID_MEASURES.GAP}px)`,
						}}
					>
						<Text
							as={'h2'}
							size={'pageTitle'}
							type={'serif'}
							weight={'light'}
							className={
								'absolute -top-[15vh] left-1/2 mx-auto w-max -translate-x-1/2 uppercase opacity-20'
							}
						>
							{t('collection.title')}
						</Text>
						<div
							className={'absolute -left-sm bottom-sm h-[60%] w-px bg-white'}
						/>
						<Text
							as={'h3'}
							size={'h6'}
							type={'serif'}
							weight={'light'}
							className={'absolute bottom-xs left-xxs'}
						>
							{t('collection.touch')}
						</Text>
						{selected && (
							<div
								className={
									'absolute -bottom-xl left-xxxs right-md h-px bg-white'
								}
							/>
						)}
						{collections.map((collection) =>
							collection.images.map((image, index) => (
								<motion.div
									key={`${collection.id}-${index}`}
									className={cn(
										'relative cursor-pointer select-none overflow-hidden transition-all duration-200 ease-in-out',
										image.className,
									)}
									style={{
										gridRow: image.area.row,
										gridColumn: image.area.column,
										zIndex: selected?.id === collection.id ? 50 : 1,
										filter:
											selected === null
												? undefined
												: selected?.id === collection.id
													? 'brightness(1)'
													: 'brightness(0.3)',
									}}
									onMouseEnter={() => handleMouseEnter(collection.id)}
									onMouseLeave={() => {
										setSelected(null)
									}}
									whileHover={{ scale: 1.02 }}
								>
									<img
										src={image.src}
										alt={collection.id}
										className="h-full w-full object-cover"
									/>
								</motion.div>
							)),
						)}
					</div>
					<div className={'min-h-[150px] px-xxl'}>
						{selected ? (
							<CollectionTable
								sections={selected.sections}
								items={selected.items}
								product={
									<h2 className={'col-span-full font-medium tracking-[0.2em]'}>
										/ {selected.name} /
									</h2>
								}
								className={'text-white'}
							/>
						) : (
							<div className={'h-full w-full'}>&nbsp;</div>
						)}
					</div>
				</AnimatePresence>
			</div>
		</LandingSection>
	)
}

export default function DressCodePage({ loaderData }: Route.ComponentProps) {
	const { t } = useTranslation('dressCode')

	const {
		navigation: { route },
	} = usePublicContext()

	useEffect(() => {
		route.setContent([
			{
				label: `Dress Code.`,
			},
		])
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	const background = useChangeBackgroundColor(extendedTheme.colors.background)

	if (!loaderData) return null

	return (
		<div>
			<section
				className={
					'false-center-on-desktop z-10 flex select-none flex-col items-center justify-center py-xxl text-brand-dusk lg:py-xxxl'
				}
			>
				<h1
					className={
						'text-center font-serif text-h2 !font-extralight min-[450px]:text-h1 md:text-mega xl:text-giga'
					}
				>
					<span className={'block'}>DRESS</span>
					<span className={'-mt-4 block'}>CODE</span>
				</h1>
				<p
					className={
						'text-[9px] font-thin uppercase tracking-[0.2em] lg:text-body-2xs xl:text-body-xs'
					}
				>
					Jaime Beriestain
				</p>
			</section>

			<section
				className={
					'grid h-[100vh] auto-rows-fr grid-cols-12 py-xl text-brand-dusk lg:grid-cols-2 lg:grid-rows-1 lg:py-xxl'
				}
			>
				<div
					className={
						'z-10 col-start-2 -col-end-2 mx-auto flex flex-col items-start gap-sm md:gap-md lg:col-start-1 lg:mt-[20vh]'
					}
				>
					<h2
						className={
							'max-w-sm font-serif text-h3 !font-extralight md:text-h2 2xl:max-w-xl 2xl:text-h1'
						}
					>
						{t('about.title')}
					</h2>
					<Text as={'p'} size={'p'} weight={'light'} className={'max-w-lg'}>
						{t('about.description')}
					</Text>
					<Button
						asChild
						variant={'link'}
						className={'px-0 font-light tracking-[0.2em] text-brand-earth'}
					>
						<a
							target={'_blank'}
							rel={'noopener nofollow'}
							href={'https://beriestain.com/'}
						>
							studio
						</a>
					</Button>
				</div>
				<div
					className={
						'relative z-0 col-start-5 -col-end-1 row-start-5 row-end-13 flex h-full w-full flex-col items-center justify-center place-self-center overflow-hidden lg:col-start-2 lg:row-start-1'
					}
				>
					<img
						alt={'Dress Code Header'}
						className={'absolute inset-0 h-full w-full object-cover'}
						src={'/img/dress-your-look/1-FOTOHEADER-DRESS CODE.webp'}
					/>
				</div>
			</section>

			<section
				className={'grid h-[100vh] grid-cols-12 grid-rows-12 py-xl lg:py-xxl'}
			>
				<div
					className={
						'relative z-0 col-start-1 -col-end-1 row-start-1 row-end-7 brightness-50 min-[450px]:row-end-8 sm:row-end-7 xl:row-start-2 xl:row-end-8'
					}
				>
					<MarketingImage
						disableHover
						alt={'Choosing the right look'}
						className={'absolute inset-0 h-full w-full object-cover'}
						src={
							'/img/dress-your-look/2-20250204_Sapienstone_Jaime_0382_OK_CMYK.webp'
						}
					/>
				</div>
				<div
					className={
						'z-10 col-start-2 -col-end-2 row-start-3 row-end-8 flex max-w-sm flex-col gap-sm border-l border-white pl-sm text-white lg:col-start-3 lg:-col-end-3 lg:max-w-lg 2xl:max-w-2xl'
					}
				>
					<h2
						className={
							'font-serif text-h3 !font-extralight md:text-h2 2xl:text-h1'
						}
					>
						{t('materials.title')}
					</h2>
					<Button
						asChild
						variant={'link'}
						className={
							'self-end px-0 py-1 font-light lowercase tracking-[0.2em] text-white'
						}
					>
						<a
							target={'_blank'}
							rel={'noopener nofollow'}
							href={
								loaderData.catalog.path
									? routes.public.document.route({
											document: loaderData.catalog.path,
										})
									: '/'
							}
						>
							{t('materials.action.discoverCatalog')}
						</a>
					</Button>
					<div className={'relative flex !h-[35vh] w-full xl:!h-[40vh]'}>
						<img
							className={'object-cover object-bottom'}
							alt={
								'Jaime Beriestain holding the Sapienstone logo made of ceramic'
							}
							src={
								'/img/dress-your-look/3-20250204_Sapienstone_Jaime_0392_OK_CMYK.webp'
							}
						/>
					</div>
				</div>
			</section>

			<LandingSection borderToBorderOnX>
				<div
					className={
						'grid grid-cols-12 grid-rows-[repeat(12,4vh)] xl:grid-cols-2'
					}
				>
					<div
						className={
							'z-10 col-start-2 -col-end-2 row-start-6 -row-end-1 mx-auto flex flex-col gap-sm text-brand-dusk sm:row-start-7 md:col-start-4 xl:col-start-1 xl:col-end-2 xl:row-start-1 xl:gap-md xl:self-end'
						}
					>
						<h2
							className={
								'font-serif text-h3 !font-extralight md:text-h2 xl:max-w-xl 2xl:text-h1'
							}
						>
							{t('suit.title')}
						</h2>
						<Text as={'p'} size={'p'} weight={'light'} className={'max-w-lg'}>
							{t('suit.description')}
						</Text>
					</div>
					<div
						className={
							'relative col-start-1 col-end-10 row-start-1 row-end-5 sm:col-end-8 sm:row-end-6 xl:col-start-2 xl:-col-end-1 xl:-row-end-1'
						}
					>
						<MarketingImage
							className={
								'absolute inset-0 h-full w-full object-cover object-[50%,65%]'
							}
							src={
								'/img/dress-your-look/4-20250205_Sapienstone_Jaime_0527_OK_CMYK.webp'
							}
						/>
					</div>
				</div>
			</LandingSection>

			<ProductHoverSection
				collections={[
					{
						id: 'fiori-di-viola-design',
						name: 'Fiori di Viola 4D',
						images: [
							{
								src: '/img/dress-your-look/collections/25_FIOR DI VIOLA 4D.webp',
								area: {
									row: '5 / -4',
									column: '4 / -8',
								},
							},
							{
								src: '/img/dress-your-look/collections/20250204_Sapienstone_Jaime_0453_OK.webp',
								area: {
									row: '3 / 6',
									column: '-4 / -1',
								},
							},
						],
						sections: ['size', 'thickness', 'finish', 'relief'] as const,
						// @ts-expect-error
						items: fioriDiViolaCollection,
					},
					{
						id: 'grigio-savelli',
						name: 'Grigio Savelli',
						images: [
							{
								src: '/img/dress-your-look/collections/25_GRIGIO SAVELLI.webp',
								area: {
									row: '3 / 9',
									column: '12 / 16',
								},
							},
							{
								src: '/img/dress-your-look/collections/25_GRIGIO SAVELLI_RILIEVO VELO_2.webp',
								area: {
									row: '6 / 9',
									column: '-4 / -1',
								},
							},
						],
						sections: ['size', 'thickness', 'finish', 'relief'] as const,
						// @ts-expect-error
						items: grigioSavelliCollection,
					},
					{
						id: 'vulca-collection',
						name: 'Vulca',
						images: [
							{
								src: '/img/dress-your-look/collections/25_VULCA BIANCO NEVE.webp',
								area: {
									row: '2 / 5',
									column: '2 / 4',
								},
								className: 'mb-xxs -translate-x-xxs aspect-square',
							},
							{
								src: '/img/dress-your-look/collections/25_VULCA SABBIA PERLATA.webp',
								area: {
									row: '2 / 5',
									column: '4 / 6',
								},
								className: 'mb-xxs -translate-x-xxs aspect-square',
							},
							{
								src: '/img/dress-your-look/collections/25_VULCA NERO VELLUTO.webp',
								area: {
									row: '2 / 5',
									column: '6 / 8',
								},
								className: 'mb-xxs -translate-x-xxs aspect-square',
							},
							{
								src: '/img/dress-your-look/collections/25_VULCA ROSSO CREMISI.webp',
								area: {
									row: '2 / 5',
									column: '8 / 10',
								},
								className: 'mb-xxs -translate-x-xxs aspect-square',
							},
						],
						sections: ['size', 'thickness', 'finish'] as const,
						// @ts-expect-error
						items: vulcaCollection,
					},
					{
						id: 'm1-m2-collection',
						name: 'Ombra di Luce',
						images: [
							{
								src: '/img/dress-your-look/collections/M1.webp',
								area: {
									row: '-5 / -1',
									column: '-13 / -10',
								},
								className: 'aspect-square',
							},
							{
								src: '/img/dress-your-look/collections/M2.webp',
								area: {
									row: '-5 / -1',
									column: '-10 / -7',
								},
								className: 'aspect-square',
							},
						],
						sections: ['size', 'thickness', 'finish', 'mosaic'] as const,
						// @ts-expect-error
						items: m1m2Collection,
					},
					{
						id: 'blu-belge-collection',
						name: 'Blu Belge',
						images: [
							{
								src: '/img/dress-your-look/collections/sambra_Blu Belge Natural_120X120_F01  base mosaico.webp',
								area: {
									row: '9 / -3',
									column: '1 / 7',
								},
							},
							{
								src: '/img/dress-your-look/collections/25_BLU BELGE_RILIEVO FILO_2.webp',
								area: {
									row: '-4 / -1',
									column: '-4 / -1',
								},
							},
						],
						sections: ['size', 'thickness', 'finish', 'relief'] as const,
						// @ts-expect-error
						items: bluBelgeCollection,
					},
					{
						id: 'crema-neve-collection',
						name: 'Crema Neve',
						images: [
							{
								src: '/img/dress-your-look/collections/25_CREMA NEVE_V2.webp',
								area: {
									row: '1 / 8',
									column: '10 / 14',
								},
								className: 'translate-x-xxs',
							},
							{
								src: '/img/dress-your-look/collections/25_CREMA NEVE_RILIEVO VELO_2.webp',
								area: {
									row: '-4 / -7',
									column: '-4 / -1',
								},
							},
						],
						sections: ['size', 'thickness', 'finish', 'relief'] as const,
						// @ts-expect-error
						items: cremaNeveCollection,
					},
				]}
			/>

			<LandingSection>
				<CollectionTable
					className={'relative lg:ml-auto lg:w-[60vw]'}
					product={
						<div className={'flex max-w-[85%] flex-col gap-sm'}>
							<div className={'grid grid-cols-[0.7fr_0.3fr] gap-sm'}>
								{[
									{
										image:
											'/img/dress-your-look/collections/25_FIOR DI VIOLA 4D.webp',
										name: 'Fiori di Viola 4D',
									},
									{
										image:
											'/img/dress-your-look/collections/20250204_Sapienstone_Jaime_0453_OK.webp',
									},
								].map((item) => (
									<div key={item.name} className={'flex flex-col gap-sm'}>
										<MarketingImage
											className={'h-[35vh] w-auto'}
											src={item.image}
											alt={item.name}
										/>
										{item.name && (
											<h2 className={'font-medium tracking-[0.2em]'}>
												/ {item.name} /
											</h2>
										)}
									</div>
								))}
							</div>
						</div>
					}
					sections={['size', 'thickness', 'finish', 'relief']}
					items={fioriDiViolaCollection}
				/>
				<p className={'mt-sm text-[10px] font-light opacity-60 lg:ml-auto lg:w-[60vw]'}>
					{t('reliefDisclaimer')}
				</p>
			</LandingSection>

			<LandingSection backgroundColor={extendedTheme.colors.brand.mist.DEFAULT}>
				<CollectionTable
					className={'relative lg:max-w-[60vw]'}
					product={
						<div className={'flex max-w-[85%] flex-col gap-sm'}>
							<div className={'grid grid-cols-[0.7fr_0.3fr] gap-sm'}>
								{[
									{
										image:
											'/img/dress-your-look/collections/25_GRIGIO SAVELLI.webp',
										name: 'Grigio Savelli',
									},
									{
										image:
											'/img/dress-your-look/collections/25_GRIGIO SAVELLI_RILIEVO VELO_2.webp',
									},
								].map((item) => (
									<div key={item.name} className={'flex flex-col gap-sm'}>
										<MarketingImage
											className={'h-[35vh] w-auto'}
											src={item.image}
											alt={item.name}
										/>
										{item.name && (
											<h2 className={'font-medium tracking-[0.2em]'}>
												/ {item.name} /
											</h2>
										)}
									</div>
								))}
							</div>
						</div>
					}
					sections={['size', 'thickness', 'finish', 'relief']}
					items={grigioSavelliCollection}
				/>
				<p className={'mt-sm text-[10px] font-light opacity-60 lg:max-w-[60vw]'}>
					{t('reliefDisclaimer')}
				</p>
			</LandingSection>

			<LandingSection>
				<CollectionTable
					className={'relative lg:ml-auto lg:w-[60vw]'}
					product={
						<div className={'flex max-w-[85%] flex-col gap-sm'}>
							<div className={'grid grid-cols-[0.7fr_0.3fr] gap-sm'}>
								{[
									{
										image:
											'/img/dress-your-look/collections/25_CREMA NEVE_V2.webp',
										name: 'Crema Neve',
									},
									{
										image:
											'/img/dress-your-look/collections/25_CREMA NEVE_RILIEVO VELO_2.webp',
									},
								].map((item) => (
									<div key={item.name} className={'flex flex-col gap-sm'}>
										<MarketingImage
											className={'h-[35vh] w-auto'}
											src={item.image}
											alt={item.name}
										/>
										{item.name && (
											<h2 className={'font-medium tracking-[0.2em]'}>
												/ {item.name} /
											</h2>
										)}
									</div>
								))}
							</div>
						</div>
					}
					sections={['size', 'thickness', 'finish', 'relief']}
					items={cremaNeveCollection}
				/>
				<p className={'mt-sm text-[10px] font-light opacity-60 lg:ml-auto lg:w-[60vw]'}>
					{t('reliefDisclaimer')}
				</p>
			</LandingSection>

			<LandingSection
				backgroundColor={extendedTheme.colors.brand.earth.DEFAULT}
			>
				<div className={'grid grid-cols-1 gap-xxxl 2xl:grid-cols-2'}>
					<CollectionTable
						className={'text-white 2xl:max-w-[40vw]'}
						product={
							<div className={'flex flex-col gap-sm'}>
								<div
									className={
										'relative grid grid-cols-[1fr_0.6fr] items-start gap-sm'
									}
								>
									{[
										{
											image:
												'/img/dress-your-look/collections/sambra_Blu Belge Natural_120X120_F01  base mosaico.webp',
											name: 'Blu Belge',
										},
										{
											image:
												'/img/dress-your-look/collections/25_BLU BELGE_RILIEVO FILO_2.webp',
										},
									].map((item) => (
										<div key={item.name} className={'flex flex-col gap-sm'}>
											<MarketingImage
												src={item.image}
												className={'h-[30vh]'}
												alt={item.name}
											/>
											{item.name && (
												<h2 className={'font-medium tracking-[0.2em]'}>
													/ {item.name} /
												</h2>
											)}
										</div>
									))}
								</div>
							</div>
						}
						sections={['size', 'thickness', 'finish', 'relief']}
						items={bluBelgeCollection}
					/>

					<CollectionTable
						className={'text-white 2xl:max-w-[40vw]'}
						product={
							<div className={'flex flex-col gap-sm'}>
								<div className={'relative flex items-start gap-sm'}>
									{[
										{
											image: '/img/dress-your-look/collections/M1.webp',
											name: 'Ombra di Luce',
										},
										{
											image: '/img/dress-your-look/collections/M2.webp',
										},
									].map((item) => (
										<div key={item.name} className={'flex flex-col gap-sm'}>
											<MarketingImage
												src={item.image}
												className={'!aspect-square max-w-[30vh]'}
												alt={item.name}
											/>
											{item.name && (
												<h2 className={'font-medium tracking-[0.2em]'}>
													/ {item.name} /
												</h2>
											)}
										</div>
									))}
								</div>
							</div>
						}
						sections={['size', 'thickness', 'finish', 'mosaic']}
						items={m1m2Collection}
					/>
				</div>
				<p className={'mt-sm text-[10px] font-light text-white opacity-60'}>
					{t('reliefDisclaimer')}
				</p>
			</LandingSection>

			<LandingSection>
				<CollectionTable
					className={'max-w-3xl'}
					product={
						<div className={'flex flex-col gap-sm'}>
							<Text as={'p'} size={'h6'} weight={'light'} type={'serif'}>
								Collezione Vulca
							</Text>

							<div className={'grid grid-cols-4 gap-sm'}>
								{[
									{
										image:
											'/img/dress-your-look/collections/25_VULCA BIANCO NEVE.webp',
										name: 'Bianco Neve',
									},
									{
										image:
											'/img/dress-your-look/collections/25_VULCA SABBIA PERLATA.webp',
										name: 'Sabbia Perlata',
									},
									{
										image:
											'/img/dress-your-look/collections/25_VULCA ROSSO CREMISI.webp',
										name: 'Rosso Cremisi',
									},
									{
										image:
											'/img/dress-your-look/collections/25_VULCA NERO VELLUTO.webp',
										name: 'Nero Velluto',
									},
								].map((item) => (
									<div key={item.name} className={'flex flex-col gap-sm'}>
										<MarketingImage src={item.image} alt={item.name} />
										<h2 className={'font-medium tracking-[0.2em]'}>
											/ {item.name} /
										</h2>
									</div>
								))}
							</div>
						</div>
					}
					sections={['size', 'thickness', 'finish']}
					items={vulcaCollection}
				/>
			</LandingSection>

			<MarketingSpacer />

			<section
				className={
					'grid grid-cols-12 grid-rows-[repeat(12,9vh)] xl:grid-rows-[repeat(12,5.5vw)]'
				}
			>
				<div
					className={cn(
						'z-10 mx-auto flex flex-col gap-sm',
						'col-start-1 -col-end-1 row-start-6 row-end-8',
						'place-content-center px-lg min-[450px]:col-end-7 min-[450px]:row-start-1 min-[450px]:row-end-8',
						'xl:row-end-6',
						'2xl:col-start-3 2xl:col-end-8',
						'text-brand-dusk',
					)}
				>
					<h2
						className={
							'font-serif text-h3 !font-extralight md:text-h2 xl:max-w-xl 2xl:text-h1'
						}
					>
						{t('dressCode.title')}
					</h2>
					<Text as={'p'} size={'p'} weight={'light'} className={'max-w-lg'}>
						<Trans>{t('dressCode.description')}</Trans>
					</Text>
				</div>

				<MarketingImage
					src={'/img/dress-your-look/casa-decor-2025/_MYC8840_RET.webp'}
					className={
						'col-start-1 -col-end-1 row-start-1 row-end-4 min-[450px]:col-start-7 min-[450px]:row-end-10 2xl:col-start-8 2xl:row-end-11'
					}
				/>

				<MarketingImage
					src={'/img/dress-your-look/casa-decor-2025/_MYC8894_RET.webp'}
					className={
						'col-start-1 col-end-6 row-start-4 -row-end-1 self-end lg:aspect-square xl:row-start-auto 2xl:col-end-5'
					}
				/>
				<img
					className={
						'col-span-3 col-start-7 -row-end-1 w-full origin-bottom-left self-end font-bold lg:scale-[0.7] xl:col-start-6 xl:pl-xxl 2xl:col-start-5 2xl:pl-xxxl'
					}
					src={CasaDecor2025}
					alt={'Casa Decor 2025'}
				/>
			</section>

			<section ref={background}>
				<MarketingSpacer side={'top'} />
				<div className={'grid grid-cols-12 gap-xxs lg:gap-sm'}>
					<MarketingImage
						className={
							'col-span-4 col-start-4 aspect-square xl:col-span-3 xl:col-start-6 xl:scale-150'
						}
						src={'/img/dress-your-look/casa-decor-2025/DRESSCODE-18_RET.webp'}
					/>
					<MarketingImage
						src={'/img/dress-your-look/casa-decor-2025/_MYC8914_RET.webp'}
						className={
							'col-span-4 col-start-8 aspect-square xl:col-span-3 xl:col-start-9 xl:scale-150'
						}
					/>
				</div>
				<MarketingSpacer side={'bottom'} />
			</section>

			<LandingSection
				borderToBorderOnX
				backgroundColor={extendedTheme.colors.brand.mist.DEFAULT}
			>
				<div
					className={
						'relative grid grid-cols-12 grid-rows-[repeat(14,8vh)] xl:grid-rows-[repeat(18,10vh)]'
					}
				>
					<img
						className={
							'absolute right-1/4 top-[10%] w-[20vw] origin-center scale-[0.8]'
						}
						src={ICGGalleryMilano}
						alt={'Iris Ceramica Group Gallery Milano'}
					/>
					<div
						className={
							'col-start-1 -col-end-1 row-start-2 -row-end-2 grid grid-cols-subgrid grid-rows-subgrid'
						}
					>
						<MarketingImage
							className={
								'col-start-2 col-end-8 row-start-1 row-end-6 min-[450px]:col-end-7 md:col-start-3 md:col-end-7 lg:col-end-6 2xl:row-end-7'
							}
							src={
								'/img/dress-your-look/icg-gallery/Carrusel_CD_Espacios_01.webp'
							}
						/>
						<MarketingImage
							className={
								'z-10 col-start-6 -col-end-1 row-start-4 row-end-12 xl:col-start-8 xl:row-end-[15]'
							}
							src={
								'/img/dress-your-look/icg-gallery/Carrusel_CD_Espacios_05.webp'
							}
						/>
						<MarketingImage
							className={
								'col-start-1 col-end-8 row-start-7 -row-end-1 min-[450px]:col-end-7 lg:col-end-6 xl:row-start-9'
							}
							src={
								'/img/dress-your-look/icg-gallery/Carrusel_CD_Espacios_04.webp'
							}
						/>
					</div>
				</div>
			</LandingSection>
		</div>
	)
}
